import { useEffect, useRef, useCallback, useReducer } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken, positionOpenerAxiosApi } from 'src/Config/axiosApi';
import {
  EnrichedEntryStatus,
  EntryOrdersResponse
} from '../types/entry.types';
import {
  LatencySample,
  recordReceived,
  useLatencyTracker
} from './useLatencyTracker';

export type { EnrichedEntryStatus } from '../types/entry.types';

export interface EntryExecutionEvent {
  ruleId: string;
  pairName: string;
  makerPlatform: string;
  makerPrice: number;
  counterPrice: number;
  contracts: number;
  tnaPercent: number;
  volumeUsd: number;
}

export interface EntryErrorEvent {
  ruleId: string;
  pairName: string;
  state: string;
  errorMessage: string;
}

type Action =
  | { type: 'STATUS'; payload: EnrichedEntryStatus[] }
  | { type: 'STATUS_UPDATE'; payload: EnrichedEntryStatus[] }
  | { type: 'EXECUTION'; payload: EntryExecutionEvent }
  | { type: 'ERROR'; payload: EntryErrorEvent }
  | { type: 'ORDERS'; payload: EntryOrdersResponse };

interface State {
  /** Map of pairId AND ruleId -> live entry state (mirrors unwinding hook) */
  states: Map<string, EnrichedEntryStatus>;
  lastExecution: EntryExecutionEvent | null;
  lastError: EntryErrorEvent | null;
  orders: EntryOrdersResponse | null;
}

const initialState: State = {
  states: new Map(),
  lastExecution: null,
  lastError: null,
  orders: null
};

/**
 * Strip the server's `_meta` envelope before passing into the reducer.
 * Mirrors `useUnwindingSocket`. Server wraps non-array payloads as
 * `{ ...payload, _meta }` and array payloads as `{ data: payload, _meta }`.
 */
function unwrap<T>(message: unknown): T {
  if (Array.isArray(message)) return message as T;
  if (
    message &&
    typeof message === 'object' &&
    '_meta' in (message as object)
  ) {
    const obj = message as Record<string, unknown> & { _meta?: unknown };
    if ('data' in obj) return obj.data as T;
    const rest = { ...obj };
    delete rest._meta;
    return rest as T;
  }
  return message as T;
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'STATUS': {
      const newMap = new Map<string, EnrichedEntryStatus>();
      for (const s of action.payload) {
        newMap.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) newMap.set(s.pairId, s);
      }
      return { ...state, states: newMap };
    }
    case 'STATUS_UPDATE': {
      const merged = new Map(state.states);
      for (const s of action.payload) {
        merged.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) merged.set(s.pairId, s);
      }
      return { ...state, states: merged };
    }
    case 'EXECUTION':
      return { ...state, lastExecution: action.payload };
    case 'ERROR':
      return { ...state, lastError: action.payload };
    case 'ORDERS':
      return { ...state, orders: action.payload };
    default:
      return state;
  }
}

export { initialState as entrySocketInitialState };

export function useEntrySocket() {
  const wsUrl = import.meta.env.VITE_POSITION_OPENER_URL;
  const accessToken = _getToken();

  const { socket } = useSocket((wsUrl || '') + '/opening', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() })
  });

  const [state, dispatch] = useReducer(reducer, initialState);
  const hasSubscribed = useRef(false);

  // REST seed: same pattern as `useUnwindingSocket` — fetch the latest
  // EnrichedEntryStatus[] snapshot when the WS isn't connected so the page
  // has data during the handshake or while the predictions subsystem is
  // OFF. Live WS deltas always override.
  useEffect(() => {
    if (!wsUrl || !accessToken) return;
    if (socket.connected) return;

    let cancelled = false;
    positionOpenerAxiosApi
      .get<EnrichedEntryStatus[]>('/states')
      .then(({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data)) return;
        dispatch({ type: 'STATUS_UPDATE', payload: data });
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('[EntrySocket] REST seed failed:', err?.message ?? err);
      });

    return () => {
      cancelled = true;
    };
  }, [wsUrl, accessToken, socket.connected]);

  const latencyBuf = useRef<LatencySample[]>([]);
  useLatencyTracker(socket, latencyBuf);

  // Subscribe on every connect/reconnect.
  useEffect(() => {
    const subscribeIfNeeded = () => {
      if (!hasSubscribed.current) {
        socket.emit('subscribe_entry');
        hasSubscribed.current = true;
      }
    };
    const markDisconnected = () => {
      hasSubscribed.current = false;
    };

    socket.on('connect', subscribeIfNeeded);
    socket.on('disconnect', markDisconnected);

    if (socket.connected) subscribeIfNeeded();
    else markDisconnected();

    return () => {
      socket.off('connect', subscribeIfNeeded);
      socket.off('disconnect', markDisconnected);
    };
  }, [socket, socket.connected]);

  const { lastMessage: statusMessage } = useSocketEvent<EnrichedEntryStatus[]>(
    socket,
    'entry_status'
  );

  const { lastMessage: statusUpdateMessage } = useSocketEvent<
    EnrichedEntryStatus[]
  >(socket, 'entry_status_update');

  const { lastMessage: executionMessage } =
    useSocketEvent<EntryExecutionEvent>(socket, 'entry_execution');

  const { lastMessage: errorMessage } = useSocketEvent<EntryErrorEvent>(
    socket,
    'entry_error'
  );

  useEffect(() => {
    if (statusMessage) {
      recordReceived(latencyBuf.current, 'entry_status', statusMessage);
      const payload = unwrap<EnrichedEntryStatus[]>(statusMessage);
      if (!Array.isArray(payload)) {
        console.warn(
          '[EntrySocket] entry_status non-array payload, ignoring:',
          payload
        );
        return;
      }
      dispatch({ type: 'STATUS', payload });
    }
  }, [statusMessage]);

  useEffect(() => {
    if (statusUpdateMessage) {
      recordReceived(
        latencyBuf.current,
        'entry_status_update',
        statusUpdateMessage
      );
      const payload = unwrap<EnrichedEntryStatus[]>(statusUpdateMessage);
      if (!Array.isArray(payload)) {
        console.warn(
          '[EntrySocket] entry_status_update non-array payload, ignoring:',
          payload
        );
        return;
      }
      dispatch({ type: 'STATUS_UPDATE', payload });
    }
  }, [statusUpdateMessage]);

  useEffect(() => {
    if (executionMessage) {
      recordReceived(latencyBuf.current, 'entry_execution', executionMessage);
      dispatch({
        type: 'EXECUTION',
        payload: unwrap<EntryExecutionEvent>(executionMessage)
      });
    }
  }, [executionMessage]);

  useEffect(() => {
    if (errorMessage) {
      recordReceived(latencyBuf.current, 'entry_error', errorMessage);
      dispatch({
        type: 'ERROR',
        payload: unwrap<EntryErrorEvent>(errorMessage)
      });
    }
  }, [errorMessage]);

  const { lastMessage: ordersMessage } = useSocketEvent<EntryOrdersResponse>(
    socket,
    'entry_orders'
  );

  useEffect(() => {
    if (ordersMessage) {
      recordReceived(latencyBuf.current, 'entry_orders', ordersMessage);
      dispatch({
        type: 'ORDERS',
        payload: unwrap<EntryOrdersResponse>(ordersMessage)
      });
    }
  }, [ordersMessage]);

  const getState = useCallback(
    (idOrPair: string): EnrichedEntryStatus | undefined =>
      state.states.get(idOrPair),
    [state.states]
  );

  const injectStates = useCallback((states: EnrichedEntryStatus[]) => {
    if (!Array.isArray(states)) {
      console.warn(
        '[EntrySocket] injectStates called with non-array, ignoring:',
        states
      );
      return;
    }
    dispatch({ type: 'STATUS_UPDATE', payload: states });
  }, []);

  return {
    isConnected: socket.connected,
    states: state.states,
    getState,
    injectStates,
    lastExecution: state.lastExecution,
    lastError: state.lastError,
    orders: state.orders
  };
}
